import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { Button, Col, Input, Modal, Row, Select, Switch } from 'antd';

import { constants } from 'helpers/constants';
import { functions } from 'helpers/functions';
import QueryString from 'query-string';
import Truncate from 'v2/components/Shared/TruncateText';
import { useListConnectOptions } from 'v2/hooks/useListConnectOptions';
import { useListTriggers } from 'v2/hooks/useListTriggers';
import { useUpdateConnectAction } from 'v2/hooks/useUpdateConnectAction';
import { connectService } from 'v2/services/connect';
import { run } from 'validations/ruleRunner';

import Validations from './validations';
import { useActionState } from '../../contexts/ActionStateContext';
import { useConnectState } from '../../contexts/ConnectStateContext';
import { getOptionParametersQuery, getOptionsParametersShouldModify } from '../../helpers/actionOptionsHelpers';

const EditAction: React.FC = () => {
  const { currentConnect } = useConnectState();
  const {
    currentAction,
    modalEditAction: modal,
    optionsAction,
    setCurrentAction,
    closeEditActionModal,
    addOptionAction,
    resetOptionAction
  } = useActionState();
  const [validationErrors, setValidationErrors] = useState<any>([]);

  const { triggers } = useListTriggers();
  const { options } = useListConnectOptions(currentConnect?.autoresponder_id);
  const { updateConnectAction, isUpdating } = useUpdateConnectAction();

  const prevCurrentActionRef = useRef<any>(currentAction);

  const errorFor = useCallback(
    (field: any) => {
      return validationErrors[field] || '';
    },
    [validationErrors]
  );

  const isValid = useCallback(
    (field: any) => {
      return validationErrors[field] ? true : false;
    },
    [validationErrors]
  );

  const handleChange = useCallback(
    (field: any, value: any) => {
      const newAction = functions.cloneDeep(currentAction);

      if (field === 'option_id') {
        const selectedOption = options && Array.isArray(options) && options.find((item: any) => item.id === value);
        const fields: Record<string, any> = {};

        if (selectedOption && selectedOption.parameters && Array.isArray(selectedOption.parameters.campos)) {
          selectedOption.parameters.campos.forEach((item: any) => {
            fields[item.nome] = newAction.options_parameters[item.nome];
          });
        }

        newAction.fields = fields;
        newAction.options_parameters = fields;
      }

      newAction[field] = value;
      setCurrentAction(functions.cloneDeep(newAction));
    },
    [setCurrentAction, currentAction, options]
  );

  const handleChangeField = useCallback(
    (field: any, value: any) => {
      const newAction = functions.cloneDeep(currentAction);

      newAction.fields = newAction.fields || {};

      if (newAction && newAction.options_parameters) {
        newAction.options_parameters[field] = String(value);
      }

      newAction.fields = newAction.options_parameters;

      setCurrentAction(functions.cloneDeep(newAction));
    },
    [setCurrentAction, currentAction]
  );

  const dispatchNewOptionParam = useCallback(
    async (autoResponderId: number, connectId: number, itemName: string, params: any) => {
      const keys = itemName.split('_');
      const toSearch = `${keys[0]}_${keys[1]}`;

      let paramsString = undefined;

      if (params && typeof params === 'object') {
        paramsString = QueryString.stringify(params);
      }

      const result = await connectService.getActionFields(autoResponderId, connectId, toSearch, paramsString);
      const fieldData = result?.data ?? result;

      addOptionAction(itemName, functions.cloneDeep(fieldData));
    },
    [addOptionAction]
  );

  const fillOptionsParametersList = useCallback(
    (optionsParametersShouldModify: string[]) => {
      const selectedOption =
        options && Array.isArray(options) && options.find((item: any) => item.id === currentAction.option_id);

      if (!optionsParametersShouldModify || optionsParametersShouldModify.length === 0) {
        return;
      }

      if (!selectedOption || !selectedOption.parameters || !Array.isArray(selectedOption.parameters.campos)) {
        return;
      }

      Promise.all(
        selectedOption.parameters.campos.map(async (item: any) => {
          if (!optionsParametersShouldModify.includes(item.nome)) {
            return;
          }

          if (item.campo_carregar) {
            const params = getOptionParametersQuery(item, selectedOption, currentAction);
            await dispatchNewOptionParam(currentConnect.autoresponder_id, currentConnect.id, item.nome, params);
            return;
          }

          await dispatchNewOptionParam(currentConnect.autoresponder_id, currentConnect.id, item.nome, undefined);
        })
      );
    },
    [options, currentAction, currentConnect, dispatchNewOptionParam]
  );

  const closeModal = useCallback(() => {
    closeEditActionModal();
  }, [closeEditActionModal]);

  const saveAction = useCallback(async () => {
    try {
      await updateConnectAction({ connectId: currentConnect.id, action: currentAction });
      closeModal();
      toast.success('Ação salva com sucesso!');
    } catch (error: any) {
      toast.error(error?.message ?? 'Erro ao salvar ação');
    }
  }, [updateConnectAction, currentConnect, currentAction, closeModal]);

  const getOptionsFieldName = useCallback((action: any, itemName: string): [string, any] => {
    if (itemName?.includes('_field_')) {
      itemName = itemName.replace('_field', '');
    }

    let value = action.options_parameters[itemName];

    if (action.fields) {
      value = action.fields[itemName];
    }

    return [itemName, value];
  }, []);

  useEffect(() => {
    const prevAction = prevCurrentActionRef.current;

    if (currentAction !== prevAction) {
      setValidationErrors(run(currentAction, Validations));
    }

    const actionOptionsHasUpdated =
      prevAction.options_parameters !== currentAction.options_parameters ||
      prevAction.option_id !== currentAction.option_id;
    const shouldUpdateOptionsParametersList =
      currentAction && currentAction.options_parameters && currentConnect && actionOptionsHasUpdated;

    const optionsParametersShouldModify = getOptionsParametersShouldModify(currentAction, prevAction, options);

    optionsParametersShouldModify.forEach((item: string) => {
      resetOptionAction(item);
    });

    if (shouldUpdateOptionsParametersList) {
      fillOptionsParametersList(optionsParametersShouldModify);
    }

    prevCurrentActionRef.current = currentAction;
  }, [currentAction, currentConnect, options, resetOptionAction, fillOptionsParametersList]);

  const currentOptions = Array.isArray(options) ? options : [];
  const selectedOption = currentOptions.find((item: any) => item.id === currentAction.option_id);

  return (
    <Modal
      open={modal}
      onCancel={closeModal}
      title={<span className='text-text-heading'>Editar Ação</span>}
      footer={
        <div className='flex items-end justify-end gap-2'>
          <Button onClick={closeModal}>Cancelar</Button>
          <Button
            type='primary'
            disabled={(validationErrors && validationErrors.length > 0) || isUpdating}
            loading={isUpdating}
            onClick={saveAction}
          >
            Salvar
          </Button>
        </div>
      }
      width={640}
    >
      <Row gutter={[16, 16]}>
        {currentConnect && currentConnect.image && (
          <Col span={24}>
            <div className='flex items-center gap-3'>
              <div
                className='mr-3 flex h-8.75 min-w-20 bg-contain bg-center bg-no-repeat'
                style={{ backgroundImage: `url(${currentConnect?.image})` }}
              />
              <div>
                <div className='font-semibold'>{currentConnect?.name}</div>
                <span className='text-sm text-gray-500'>{currentConnect?.description}</span>
                <div className='mt-1 text-sm'>
                  <strong>{currentConnect?.parameters[0]?.label}</strong>
                </div>
                <Truncate count={50} final='...'>
                  {currentConnect?.parameters[0]?.value}
                </Truncate>
              </div>
            </div>
          </Col>
        )}

        <Col xs={24} md={6}>
          <div className='flex flex-col items-start'>
            <span className='mb-1'>{currentAction.active ? 'Ativado' : 'Desativado'}</span>
            <Switch checked={currentAction.active} onChange={(checked: boolean) => handleChange('active', checked)} />
          </div>
        </Col>
        <Col xs={24} md={18}>
          <div>
            <label className='mb-1 block text-sm'>Título da ação</label>
            <Input
              placeholder='Digite o título da ação'
              value={currentAction.description}
              status={isValid('description') ? 'error' : undefined}
              onChange={(e: any) => handleChange('description', e.target.value)}
            />
            {isValid('description') && <span className='text-xs text-red-500'>{errorFor('description')}</span>}
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div>
            <label className='mb-1 block text-sm'>Se isso acontecer:</label>
            <Select
              className='w-full'
              value={currentAction.trigger_id}
              onChange={(value: any) => handleChange('trigger_id', value)}
              status={isValid('trigger_id') ? 'error' : undefined}
              options={
                triggers && Array.isArray(triggers)
                  ? triggers.map((item: any) => ({ value: item.id, label: item.name }))
                  : []
              }
            />
            {isValid('trigger_id') && <span className='text-xs text-red-500'>{errorFor('trigger_id')}</span>}
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div>
            <label className='mb-1 block text-sm'>Faça isso:</label>
            <Select
              className='w-full'
              value={currentAction.option_id}
              onChange={(value: any) => handleChange('option_id', value)}
              status={isValid('option_id') ? 'error' : undefined}
              disabled={!(options && Array.isArray(options))}
              options={currentOptions.map((option: any) => ({ value: option.id, label: option.title }))}
            />
            {isValid('option_id') && <span className='text-xs text-red-500'>{errorFor('option_id')}</span>}
          </div>
        </Col>
        {currentAction.trigger_id &&
          currentAction.option_id &&
          currentAction.options_parameters &&
          selectedOption &&
          selectedOption.parameters &&
          Array.isArray(selectedOption.parameters.campos) &&
          selectedOption.parameters.campos.map((item: any) => {
            const [itemName, itemValue] = getOptionsFieldName(currentAction, item.nome);
            return item.tipo === 'list' || item.tipo === 'listdinamico' ? (
              <Col span={24} key={`fieldAction-${item.nome}`}>
                <div>
                  <label className='mb-1 block text-sm'>{item.label}</label>
                  <Select
                    className='w-full'
                    value={String(currentAction.options_parameters[item.nome])}
                    onChange={(value: any) => handleChangeField(item.nome, value)}
                    disabled={
                      !currentAction.options_parameters ||
                      !Array.isArray(optionsAction[item.nome]) ||
                      optionsAction[item.nome].length <= 0
                    }
                    options={
                      optionsAction[item.nome] &&
                      Array.isArray(optionsAction[item.nome]) &&
                      optionsAction[item.nome].length > 0
                        ? optionsAction[item.nome].map((opt: any) => ({ value: String(opt.id), label: opt.name }))
                        : []
                    }
                  />
                  {item.tooltip && <span className='text-xs text-gray-500'>{item.tooltip}</span>}
                </div>
                {currentConnect &&
                  currentConnect.autoresponder_id &&
                  currentConnect.autoresponder_id === constants.MAUTIC_AUTORESPONDER_ID && (
                    <span className='text-xs text-gray-500'>
                      * Caso a listagem não esteja carregando, limpe o cache do seu Mautic ou procure ajuda na&nbsp;
                      <a href='https://mautic.org/support' rel='noopener noreferrer' target='_blank'>
                        página de suporte do Mautic.
                      </a>
                    </span>
                  )}
              </Col>
            ) : (
              <Col span={24} key={`fieldAction-${item.nome}`}>
                <div>
                  <label className='mb-1 block text-sm'>{item.label}</label>
                  <Input value={itemValue} onChange={(e: any) => handleChangeField(itemName, e.target.value)} />
                </div>
              </Col>
            );
          })}
      </Row>
    </Modal>
  );
};

export default EditAction;
